import { useState, useEffect, useCallback } from 'react'
import { X, Info, ExternalLink, Image as ImageIcon, Sparkles } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { storage } from '@/lib/storage'
import { analyticsService } from '@/services/analytics'

export interface BannerAction {
  label: string
  navigationUrl?: string
  isPrimary?: boolean
  onPressed?: () => void
}

export interface BannerConfig {
  id: string
  title?: string
  message: string
  imageUrl?: string
  actions?: BannerAction[]
  showOnce?: boolean
  autoDismissSeconds?: number
  isActive?: boolean
  accent?: string
}

interface InfoBannerProps {
  banner: BannerConfig
  commercialId?: string
  onDismiss?: () => void
}

export function InfoBanner({ banner, commercialId = '', onDismiss }: InfoBannerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    if (!banner || !banner.isActive) return
    const bannerKey = `${banner.id}_shown_${commercialId}`
    const hasBeenShown = storage.read<boolean>(bannerKey)
    if (banner.showOnce && hasBeenShown) return

    const timer = setTimeout(() => {
      setIsOpen(true)
      analyticsService.trackEvent('banner_shown', {
        banner_id: banner.id,
        banner_title: banner.title,
        commercial_id: commercialId,
      })
    }, 3000)

    return () => clearTimeout(timer)
  }, [banner, commercialId])

  useEffect(() => {
    if (!banner?.autoDismissSeconds || !isOpen) return
    const timer = setTimeout(() => {
      handleDismiss()
    }, banner.autoDismissSeconds * 1000)
    return () => clearTimeout(timer)
  }, [banner, isOpen])

  const handleDismiss = useCallback(() => {
    setIsOpen(false)
    if (banner?.showOnce) {
      const bannerKey = `${banner.id}_shown_${commercialId}`
      storage.write(bannerKey, true)
    }
    analyticsService.trackEvent('banner_dismissed', { banner_id: banner?.id })
    onDismiss?.()
  }, [banner, commercialId, onDismiss])

  const handleAction = useCallback((action: BannerAction) => {
    analyticsService.trackEvent('banner_action_clicked', {
      banner_id: banner?.id,
      action_label: action.label,
    })
    if (action.onPressed) {
      action.onPressed()
    } else if (action.navigationUrl) {
      window.open(action.navigationUrl, '_blank')
    }
    if (banner?.showOnce) handleDismiss()
  }, [banner, handleDismiss])

  if (!banner || !banner.isActive) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="flex items-center gap-2">
          <div className="p-2 rounded-full bg-brand-blue/10 text-brand-blue">
            <Info className="w-5 h-5" />
          </div>
          <span>{banner.title || 'Information'}</span>
        </DialogTitle>
        <DialogDescription className="space-y-4">
          {banner.imageUrl && !imageError && (
            <div className="relative w-full h-40 rounded-xl overflow-hidden bg-muted">
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
              <img
                src={banner.imageUrl}
                alt={banner.title || 'Banner'}
                className={`w-full h-full object-cover transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </div>
          )}
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{banner.message}</p>
          
          {banner.actions && banner.actions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {banner.actions.map((action, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleAction(action)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    action.isPrimary
                      ? 'bg-brand-blue text-white hover:bg-brand-blue-light'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {action.label}
                  {action.navigationUrl && <ExternalLink className="w-3.5 h-3.5" />}
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  )
}

export const InfoBannerService = {
  showBannerIfActive: (
    banner: BannerConfig | null | undefined,
    commercialId: string,
    showBanner: (banner: BannerConfig) => void
  ): boolean => {
    if (!banner?.isActive) return false

    const bannerKey = `${banner.id}_shown_${commercialId}`
    const hasBeenShown = storage.read<boolean>(bannerKey)

    if (banner.showOnce && hasBeenShown) return false

    showBanner(banner)
    return true
  },
}

export const useAutoShowBanner = () => {
  const [banner, setBanner] = useState<BannerConfig | null>(null)
  const showBanner = useCallback((b: BannerConfig) => setBanner(b), [])
  return { banner, showBanner }
}
